export interface QuizQuestion {
  id: number;
  text: string;
  options: {
    text: string;
    archetype: Archetype;
  }[];
}

export type Archetype = 'Vishnu' | 'Lakshmi' | 'Ketu' | 'Rahu' | 'Alakshmi' | 'Eris';

export const archetypeDescriptions: Record<Archetype, string> = {
  Vishnu: "You are a natural leader with immense courage and determination. Your strength lies in facing challenges head-on and inspiring others to be brave.",
  Lakshmi: "You possess deep wisdom and a thirst for knowledge. Your ability to analyze situations and provide insights makes you a valuable guide to those around you.",
  Ketu: "You have a tendency to avoid responsibility and may feel disconnected from reality at times. Your journey is about finding purpose and grounding yourself.",
  Rahu: "You have a strong materialistic nature that can lead to possessiveness and greed. Your path is about finding balance and letting go of excessive desires.",
  Alakshmi: "You may struggle with motivation and feel withdrawn from others. Your journey is about finding inner strength and reconnecting with your true self.",
  Eris: "You have a playful and mischievous nature that can sometimes cause chaos. Your path is about channeling this energy into positive creativity and fun."
};

// Mapping of options to archetypes
export const optionMapping: Record<string, Archetype> = {
  'A': 'Vishnu',
  'B': 'Lakshmi',
  'C': 'Rahu',
  'D': 'Ketu',
  'E': 'Alakshmi',
  'F': 'Eris'
};

export const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    text: "Your friend is crying because they lost their pencil. What do you do?",
    options: [
      { text: "Say kind words and help find it.", archetype: "Vishnu" },
      { text: "Offer your extra pencil to them.", archetype: "Lakshmi" },
      { text: "Say \"It's just a pencil. Stop crying.\"", archetype: "Ketu" },
      { text: "Take someone else's pencil and give that.", archetype: "Rahu" },
      { text: "Pretend you didn't see them.", archetype: "Alakshmi" },
      { text: "Laugh and hide another pencil for fun.", archetype: "Eris" }
    ]
  },
  {
    id: 2,
    text: "You see trash in your school playground. What do you do?",
    options: [
      { text: "Tell everyone to keep the area clean.", archetype: "Vishnu" },
      { text: "Pick it up and throw it in the bin.", archetype: "Lakshmi" },
      { text: "Say it's the cleaner's job, not yours.", archetype: "Ketu" },
      { text: "Add more trash and walk away.", archetype: "Rahu" },
      { text: "Think \"It's always dirty, nothing will change.\"", archetype: "Alakshmi" },
      { text: "Kick the trash like a football.", archetype: "Eris" }
    ]
  },
  {
    id: 3,
    text: "Your classmate doesn't understand a math problem. How do you act?",
    options: [
      { text: "Help explain it kindly.", archetype: "Vishnu" },
      { text: "Share your notes or tricks that helped you.", archetype: "Lakshmi" },
      { text: "Say, \"I already finished mine, you figure it out.\"", archetype: "Ketu" },
      { text: "Give them wrong answers to finish fast.", archetype: "Rahu" },
      { text: "Say, \"This is boring, I don't care.\"", archetype: "Alakshmi" },
      { text: "Distract them with jokes so they forget.", archetype: "Eris" }
    ]
  },
  {
    id: 4,
    text: "At a group project, what's your role?",
    options: [
      { text: "You coordinate and make sure everyone is included.", archetype: "Vishnu" },
      { text: "You bring charts, tools, or resources to support.", archetype: "Lakshmi" },
      { text: "You do the minimum and say you're busy.", archetype: "Ketu" },
      { text: "You take all credit even if others worked.", archetype: "Rahu" },
      { text: "You say \"This is not important\" and skip it.", archetype: "Alakshmi" },
      { text: "You make funny edits and mess up the work.", archetype: "Eris" }
    ]
  },
  {
    id: 5,
    text: "You see someone being bullied online. What do you do?",
    options: [
      { text: "Message the victim with support and report the bully.", archetype: "Vishnu" },
      { text: "Share a helpful or uplifting post.", archetype: "Lakshmi" },
      { text: "Ignore and scroll past saying \"Not my problem.\"", archetype: "Ketu" },
      { text: "Like or laugh at the mean comment.", archetype: "Rahu" },
      { text: "Think \"They must have deserved it\" and move on.", archetype: "Alakshmi" },
      { text: "Join in the teasing for fun.", archetype: "Eris" }
    ]
  },
  {
    id: 6,
    text: "Your sibling breaks your toy by mistake. What do you do?",
    options: [
      { text: "Laugh and break theirs too.", archetype: "Eris" },
      { text: "Forgive them and say, \"Let's play together anyway.\"", archetype: "Vishnu" },
      { text: "Tell them it's okay, but feel upset and distant.", archetype: "Alakshmi" },
      { text: "Take their toy quietly and hide it.", archetype: "Rahu" },
      { text: "Say nothing and walk away sadly.", archetype: "Ketu" },
      { text: "Fix it or try to share something else with them.", archetype: "Lakshmi" }
    ]
  },
  {
    id: 7,
    text: "Your teacher praises another student and not you. What do you think?",
    options: [
      { text: "That's smart! I'll try the same next time.", archetype: "Rahu" },
      { text: "Maybe they needed some love... I'll talk later.", archetype: "Vishnu" },
      { text: "Stay silent and avoid that friend from now on.", archetype: "Alakshmi" },
      { text: "Try doing something good to help them shine instead.", archetype: "Lakshmi" },
      { text: "Call them out in class and laugh.", archetype: "Eris" },
      { text: "Feel sad and lose interest in friendships.", archetype: "Ketu" }
    ]
  },
  {
    id: 8,
    text: "You are given a group task, but others are slow. What will you do?",
    options: [
      { text: "Motivate them gently to keep trying.", archetype: "Vishnu" },
      { text: "Start spreading rumors that they're lazy.", archetype: "Rahu" },
      { text: "Do it alone and take all the credit.", archetype: "Rahu" },
      { text: "Share your work with them and explain parts.", archetype: "Lakshmi" },
      { text: "Say \"They're hopeless\" and give up too.", archetype: "Alakshmi" },
      { text: "Keep quiet and let everyone fail together.", archetype: "Ketu" }
    ]
  },
  {
    id: 9,
    text: "You want a new gadget, but your parents say no. Your reaction?",
    options: [
      { text: "Understand their reason and thank them anyway.", archetype: "Vishnu" },
      { text: "Complain loudly and say they don't care.", archetype: "Rahu" },
      { text: "Break something in anger.", archetype: "Eris" },
      { text: "Sneak into their wallet to get money.", archetype: "Rahu" },
      { text: "Accept it, and try using the old one creatively.", archetype: "Lakshmi" },
      { text: "Feel helpless and sulk the whole day.", archetype: "Alakshmi" }
    ]
  },
  {
    id: 10,
    text: "Your team loses a school competition. What do you do?",
    options: [
      { text: "Blame the captain and shout at everyone.", archetype: "Eris" },
      { text: "Smile and cheer the winners.", archetype: "Vishnu" },
      { text: "Tear up the project in frustration.", archetype: "Eris" },
      { text: "Reflect on mistakes and suggest improvements.", archetype: "Lakshmi" },
      { text: "Think \"I'm never joining again.\"", archetype: "Alakshmi" },
      { text: "Say \"I wasn't even interested anyway.\"", archetype: "Ketu" }
    ]
  },
  {
    id: 11,
    text: "You see someone crying alone during lunch. What do you do?",
    options: [
      { text: "Laugh and ask if they lost their toy.", archetype: "Eris" },
      { text: "Sit quietly and feel sad but say nothing.", archetype: "Ketu" },
      { text: "Offer your food and ask what happened.", archetype: "Lakshmi" },
      { text: "Ignore them. Not your problem.", archetype: "Alakshmi" },
      { text: "Share a joke or game to cheer them up.", archetype: "Vishnu" },
      { text: "Tease them in front of others.", archetype: "Eris" }
    ]
  },
  {
    id: 12,
    text: "Your friend lies to get attention from the teacher. What do you feel?",
    options: [
      { text: "That's smart! I'll try the same next time.", archetype: "Rahu" },
      { text: "Maybe they needed some love... I'll talk later.", archetype: "Vishnu" },
      { text: "Stay silent and avoid that friend from now on.", archetype: "Alakshmi" },
      { text: "Try doing something good to help them shine instead.", archetype: "Lakshmi" },
      { text: "Call them out in class and laugh.", archetype: "Eris" },
      { text: "Feel sad and lose interest in friendships.", archetype: "Ketu" }
    ]
  },
  {
    id: 13,
    text: "You accidentally spill milk on your notebook. How do you react?",
    options: [
      { text: "Cry and stop doing any homework now.", archetype: "Alakshmi" },
      { text: "Try to clean it and rewrite.", archetype: "Lakshmi" },
      { text: "Blame your sibling for distracting you.", archetype: "Rahu" },
      { text: "Smile and say, \"Oops! I'll do it again.\"", archetype: "Vishnu" },
      { text: "Pretend it never happened and hide the book.", archetype: "Ketu" },
      { text: "Rip the rest of the notebook out of anger.", archetype: "Eris" }
    ]
  },
  {
    id: 14,
    text: "Your teammate forgets an important step in a science project.",
    options: [
      { text: "Mock them and tell everyone about the mistake.", archetype: "Eris" },
      { text: "Take over their job and scold them later.", archetype: "Rahu" },
      { text: "Feel frustrated but say nothing and keep distance.", archetype: "Ketu" },
      { text: "Help them revise it with patience.", archetype: "Lakshmi" },
      { text: "Stay quiet and skip the next team activity.", archetype: "Alakshmi" },
      { text: "Guide them to redo it in a fun way.", archetype: "Vishnu" }
    ]
  },
  {
    id: 15,
    text: "A classmate wins a prize you wanted badly. How do you respond?",
    options: [
      { text: "Walk up and say, \"Congrats, you earned it!\"", archetype: "Vishnu" },
      { text: "Feel cold inside but fake a smile.", archetype: "Alakshmi" },
      { text: "Plan to beat them next time, no matter what.", archetype: "Rahu" },
      { text: "Try to learn their method and improve.", archetype: "Lakshmi" },
      { text: "Shrug, \"Whatever. Awards are stupid anyway.\"", archetype: "Ketu" },
      { text: "Tell others they cheated or had help.", archetype: "Eris" }
    ]
  },
  {
    id: 16,
    text: "You get a low grade on something you worked hard on. What next?",
    options: [
      { text: "Tear the paper and throw it at your teacher.", archetype: "Eris" },
      { text: "Think, \"I must not be good at anything.\"", archetype: "Alakshmi" },
      { text: "Ask how you can improve and try again.", archetype: "Vishnu" },
      { text: "Cheat next time to score higher.", archetype: "Rahu" },
      { text: "Skip the next test altogether.", archetype: "Ketu" },
      { text: "Practice more with better notes and help.", archetype: "Lakshmi" }
    ]
  },
  {
    id: 17,
    text: "You find a lost pencil box that's very expensive. What do you do?",
    options: [
      { text: "Keep it. Finders keepers.", archetype: "Rahu" },
      { text: "Imagine it was gifted to you by God.", archetype: "Ketu" },
      { text: "Hand it to the teacher to return.", archetype: "Vishnu" },
      { text: "Break it and pretend you never saw it.", archetype: "Eris" },
      { text: "Keep it in your bag and say nothing.", archetype: "Alakshmi" },
      { text: "Check if it has a name and return it cheerfully.", archetype: "Lakshmi" }
    ]
  },
  {
    id: 18,
    text: "Your sibling or friend gets all the attention today. You...",
    options: [
      { text: "Trip them \"accidentally.\"", archetype: "Eris" },
      { text: "Walk away feeling lonely and forgotten.", archetype: "Alakshmi" },
      { text: "Smile and let them enjoy their moment.", archetype: "Vishnu" },
      { text: "Hoard your toys and refuse to share.", archetype: "Rahu" },
      { text: "Sulk in a corner and say you're sick.", archetype: "Ketu" },
      { text: "Draw them a card to celebrate.", archetype: "Lakshmi" }
    ]
  },
  {
    id: 19,
    text: "Someone new joins your class and looks different.",
    options: [
      { text: "Ask them to sit beside you.", archetype: "Vishnu" },
      { text: "Whisper with your friends and giggle.", archetype: "Eris" },
      { text: "Give them your old book and welcome them.", archetype: "Lakshmi" },
      { text: "Tell them they won't fit in here.", archetype: "Rahu" },
      { text: "Stay quiet and never talk to them.", archetype: "Ketu" },
      { text: "Avoid school that day to not deal with new people.", archetype: "Alakshmi" }
    ]
  },
  {
    id: 20,
    text: "You're told to clean up your toys. You feel...",
    options: [
      { text: "\"Why always me?\" and hide under the table.", archetype: "Ketu" },
      { text: "Throw the toys around and say \"I quit!\"", archetype: "Eris" },
      { text: "Think you're too special to clean.", archetype: "Rahu" },
      { text: "Clean them up and help your sibling too.", archetype: "Lakshmi" },
      { text: "Bargain: \"I'll clean if you give me chocolate.\"", archetype: "Vishnu" },
      { text: "Sing while sorting and place each one carefully.", archetype: "Vishnu" }
    ]
  }
];
